// src/config/branding.ts
//
// Hostname-basiertes Branding, das zur Laufzeit aufgelöst wird. Dasselbe gebaute
// Frontend wird unter mehreren Domains ausgeliefert (z.B. taskmanager.csnetworkx.de
// und layout.csnetworkx.de) – hier wird anhand von window.location.hostname
// entschieden, welches Branding die Anmeldemaske zeigt.

export type BrandKey = 'default' | 'layout';

export type BrandAccent = 'indigo' | 'emerald';

export interface Branding {
  key: BrandKey;
  /** Generischer App-Name (Tab-Titel / PWA). */
  appName: string;
  /** Überschrift der Login-Maske. */
  loginHeading: string;
  /** Welches Icon im Login-Tile gerendert wird. */
  loginIcon: 'store' | 'layout';
  /** Akzentfarbe für Login-Buttons/Fokus/Icon. */
  accent: BrandAccent;
}

/** Hosts, die das Layoutdesigner-Branding erhalten. */
const LAYOUT_HOSTS: readonly string[] = ['layout.csnetworkx.de'];

const DEFAULT_BRANDING: Branding = {
  key: 'default',
  appName: 'Store Portal',
  loginHeading: 'Anmelden bei Store Portal',
  loginIcon: 'store',
  accent: 'indigo',
};

const LAYOUT_BRANDING: Branding = {
  key: 'layout',
  appName: 'Store Portal',
  loginHeading: 'Layoutdesigner',
  loginIcon: 'layout',
  accent: 'emerald',
};

/**
 * Ermittelt das Branding für den aktuellen Host.
 */
export function getBranding(): Branding {
  const host =
    typeof window !== 'undefined' ? window.location.hostname : '';
  if (LAYOUT_HOSTS.includes(host)) {
    return LAYOUT_BRANDING;
  }
  return DEFAULT_BRANDING;
}

// Vollständige Tailwind-Klassenstrings pro Akzent. Tailwind erlaubt keine
// dynamisch zusammengesetzten Klassennamen, daher werden hier vollständige
// Klassen hinterlegt, aus denen zur Laufzeit ausgewählt wird.
export const ACCENT_CLASSES: Record<
  BrandAccent,
  { input: string; button: string; tile: string }
> = {
  indigo: {
    input: 'focus:border-indigo-500 focus:ring-indigo-500',
    button:
      'bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 focus-visible:outline-indigo-500',
    tile: 'text-indigo-600',
  },
  emerald: {
    input: 'focus:border-emerald-500 focus:ring-emerald-500',
    button:
      'bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 focus-visible:outline-emerald-500',
    tile: 'text-emerald-600',
  },
};
